import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    hashKey,
    keepPreviousData,
    type QueryKey,
    useQueries,
    useQuery,
} from "@tanstack/react-query";
import type {
    GetTransactionsResponse,
    TransactionListItem,
} from "@common/schemas/transaction.schema";
import {
    summarizeBatchResults,
    useRetryFailedBatches,
} from "@hooks/loadMoreBatchResults";
import {
    buildSnapshotBatchQuery,
    buildSnapshotQuery,
    type SnapshotQueryParams,
} from "./transactionsSnapshotQueries";
import {
    chunkSnapshotIds,
    getSnapshotHasMore,
    mergeSnapshotRows,
    orderRowsByIds,
} from "./useTransactionsSnapshotRows.lib";
import {
    TRANSACTIONS_CACHE_DURATION,
    TRANSACTIONS_PAGE_SIZE,
} from "./useTransactionsQuery";

interface UseTransactionsSnapshotRowsParams extends SnapshotQueryParams {
    enabled?: boolean;
}

export interface TransactionsSnapshotRows {
    /** Every loaded row; `undefined` until the snapshot arrives. */
    rows: TransactionListItem[] | undefined;
    /** Also true while a requested batch is loading or failed. */
    hasMore: boolean;
    isLoadingMore: boolean;
    isLoadMoreError: boolean;
    isFirstPageFetching: boolean;
    loadNextPage: () => void;
    retry: () => void;
    /** Keys of the cache entries holding rows, for writers that patch them. */
    loadedQueryKeys: QueryKey[];
    /** Changes whenever the list starts over from the first batch. */
    resetKey: string;
    /** The snapshot stopped at the server's cap, so more rows match. */
    isTruncated: boolean;
}

const NO_IDS: number[] = [];
const NO_RESPONSES: ShownBatches["responses"] = [];

const LIST_QUERY_OPTIONS = {
    staleTime: TRANSACTIONS_CACHE_DURATION,
    refetchOnWindowFocus: false,
};

interface ShownBatches {
    resetKey: string;
    responses: Array<GetTransactionsResponse | undefined>;
}

/**
 * Loads a transaction list from the ordered ids of every match, taken once
 * when the list opens: the first batch comes with the ids, and each later
 * batch loads the next ids by id. Rows leaving the filtered list (a review,
 * a hide) never shift later batches, so nothing is skipped. Anything that
 * changes the snapshot key (filters, sort, scope) starts over from batch 1;
 * the previous rows stay until the new snapshot arrives.
 */
export function useTransactionsSnapshotRows({
    enabled = true,
    ...params
}: UseTransactionsSnapshotRowsParams): TransactionsSnapshotRows {
    const snapshotQuery = buildSnapshotQuery(params);
    const resetKey = hashKey(snapshotQuery.queryKey);
    const [batchState, setBatchState] = useState({
        resetKey,
        loadedBatchCount: 1,
    });
    const isCurrentList = batchState.resetKey === resetKey;
    if (!isCurrentList) {
        setBatchState({ resetKey, loadedBatchCount: 1 });
    }
    const loadedBatchCount = isCurrentList ? batchState.loadedBatchCount : 1;

    const snapshot = useQuery({
        ...snapshotQuery,
        ...LIST_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        enabled,
    });
    const currentSnapshot = snapshot.isPlaceholderData
        ? undefined
        : snapshot.data;
    const ids = currentSnapshot?.ids ?? NO_IDS;

    const batchIdLists = useMemo(
        () =>
            chunkSnapshotIds({
                ids: ids.slice(
                    TRANSACTIONS_PAGE_SIZE,
                    loadedBatchCount * TRANSACTIONS_PAGE_SIZE,
                ),
                pageSize: TRANSACTIONS_PAGE_SIZE,
            }),
        [ids, loadedBatchCount],
    );
    const batchQueries = batchIdLists.map((batchIds) =>
        buildSnapshotBatchQuery({ ...params, batchIds }),
    );
    // A refresh gives every batch new ids, so useQueries starts each one
    // without data; the batch's last rows hold its place until it reloads.
    const shownBatchesRef = useRef<ShownBatches>({ resetKey, responses: [] });
    const shownResponses =
        shownBatchesRef.current.resetKey === resetKey
            ? shownBatchesRef.current.responses
            : NO_RESPONSES;
    const {
        responses,
        placeholderFlags,
        isAnyBatchPending,
        isAnyBatchError,
        failedRefetches,
    } = useQueries({
        queries: batchQueries.map((query, index) => ({
            ...query,
            ...LIST_QUERY_OPTIONS,
            placeholderData: shownResponses[index],
            enabled,
        })),
        combine: summarizeBatchResults<GetTransactionsResponse>,
    });
    useEffect(() => {
        shownBatchesRef.current = {
            resetKey,
            responses: responses.map((response, index) =>
                placeholderFlags[index] || response === undefined
                    ? shownResponses[index]
                    : {
                          ...response,
                          data: orderRowsByIds({
                              ids: batchIdLists[index],
                              rows: response.data,
                          }),
                      },
            ),
        };
    }, [resetKey, responses, placeholderFlags, shownResponses, batchIdLists]);

    const snapshotRows = snapshot.data?.data;
    const rows = useMemo(
        () =>
            snapshotRows === undefined
                ? undefined
                : mergeSnapshotRows({
                      firstRows: snapshotRows,
                      batches: batchIdLists.map((batchIds, index) => {
                          const batchRows = responses[index]?.data;
                          return {
                              ids:
                                  placeholderFlags[index] && batchRows
                                      ? batchRows.map(({ id }) => id)
                                      : batchIds,
                              rows: batchRows,
                          };
                      }),
                  }),
        [snapshotRows, batchIdLists, responses, placeholderFlags],
    );

    const loadedQueryKeys = [
        ...(currentSnapshot === undefined ? [] : [snapshotQuery.queryKey]),
        ...batchQueries
            .filter(
                (_, index) =>
                    responses[index] !== undefined && !placeholderFlags[index],
            )
            .map((query) => query.queryKey),
    ];

    const isNextBatchRequested = isAnyBatchPending || isAnyBatchError;
    const hasMore =
        currentSnapshot !== undefined &&
        (isNextBatchRequested ||
            getSnapshotHasMore({
                idCount: ids.length,
                loadedBatchCount,
                pageSize: TRANSACTIONS_PAGE_SIZE,
            }));
    const canLoadNextPage = hasMore && !isNextBatchRequested;

    const loadNextPage = useCallback(() => {
        if (!canLoadNextPage) {
            return;
        }
        setBatchState((previous) =>
            previous.resetKey === resetKey &&
            previous.loadedBatchCount === loadedBatchCount
                ? { resetKey, loadedBatchCount: loadedBatchCount + 1 }
                : previous,
        );
    }, [canLoadNextPage, resetKey, loadedBatchCount, setBatchState]);

    const retry = useRetryFailedBatches(failedRefetches);

    return {
        rows,
        hasMore,
        isLoadingMore: isAnyBatchPending,
        isLoadMoreError: isAnyBatchError,
        isFirstPageFetching: snapshot.isFetching,
        loadNextPage,
        retry,
        loadedQueryKeys,
        resetKey,
        isTruncated: currentSnapshot?.isTruncated ?? false,
    };
}
